import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BillPageLayout from "../bills/component/layout/BillPageLayout";
import { useGetBulkPurchaseByIdQuery, useLazyDownloadBulkPurchaseInvoiceQuery } from "../../../service/bulk-purchase-service";
import { useConfigLayout } from "../../../hooks/useConfigLayout";
import { BackIcon, SearchIcon, EyeIcon, DownloadIcon } from "../../../assets/icons";
import { BillingDetails, BulkPurchaseTransaction, VendingDetails } from "../../../model/bulk-purchase/bulk-purchase";
import Button from "../../../common/components/xepp/button/Button";
import { TablePagination } from "../../../common/components/xepp/table-pagination/TablePagination";
import { ApplicationRoutes } from "../../../routes";
import styles from "./BulkPurchaseDetailsPage.module.scss";
import { toast } from "react-toastify";
import { useNotifyBillingMutation, useReVendMutation } from "../../../service/vending-service";
import { getErrorMessage } from "../../../utils/error-handler";
import CommonSideDialog from "../../../common/components/side-dialog/CommonSideDialog";
import TransactionDetails from "../../../common/components/xepp/transaction-details/TransactionDetails";
import { mapNotifyTimeline, mapTokenTimeline } from "../../../common/components/xepp/transaction-details/timeline";
import { TransactionDetailData } from "../../../common/components/xepp/transaction-details/types";
import { CombinedDetail } from "./types";
import { formatDateTime, formatMoney, getStatusLabel, getStatusVariant } from "./utils";

type RouteParams = {
	reference?: string;
};

type BulkPurchaseLocationState = {
	bulkPurchaseSearch?: string;
} | null;

const DETAILS_PAGE_SIZE = 10;

const mapBillingDetailToRow = (billing: BillingDetails, fallbackId?: string): CombinedDetail => ({
	id: billing.id || fallbackId || `billing-${billing.accountNumber || billing.meterNumber || "unknown"}`,
	accountNumber: billing.accountNumber || "-",
	meterNumber: billing.meterNumber || billing.accountNumber || "-",
	accountName: billing.accountName || billing.customerId || "-",
	address: billing.address || "-",
	typeLabel: "Postpaid",
	createdAt: billing.createdAt,
	amount: billing.amount,
	billingDetail: billing,
});

const mapVendingDetailToRow = (
	vendingDetail: VendingDetails,
	fallbackId: string
): CombinedDetail => ({
	id: vendingDetail.id || fallbackId,
	accountNumber: vendingDetail.accountNumber || "-",
	meterNumber: vendingDetail.meterNumber || vendingDetail.accountNumber || "-",
	accountName: vendingDetail.accountName || "-",
	address: vendingDetail.address || "-",
	typeLabel: "Prepaid",
	createdAt: vendingDetail.createdAt,
	amount: (vendingDetail as unknown as { amount?: { currency: string; amount: number } })?.amount,
	vendingDetail,
});

// Mirrors TransactionDetailsContainer's mapping so a bulk sub-transaction renders in the
// same details component (overview + timeline + actions) as single prepaid/postpaid history.
const mapDetailToTransactionData = (detail: CombinedDetail, transaction: BulkPurchaseTransaction): TransactionDetailData => {
	const vending = detail.vendingDetail;
	const billing = detail.billingDetail;
	const notifyAttempts = billing?.notifyTimeline ?? [];

	return {
		date: detail.createdAt ?? transaction.createdAt ?? "",
		time: detail.createdAt ?? transaction.createdAt ?? "",
		customerName: detail.accountName,
		amount: formatMoney(detail.amount) ?? "N/A",
		paymentStatus: vending?.paymentStatus ?? billing?.paymentStatus ?? transaction.status ?? 0,
		transactionId: detail.id,
		unitBought: vending?.vendingToken?.totalKwh?.toString(),
		paymentChannel: transaction.type ?? 0,
		token: vending?.vendingToken?.token ?? transaction.externalReference ?? "",
		mapRefundToken: vending?.vendingToken?.mapRefundToken,
		vendingStatus: vending?.vendingToken?.vendingStatus,
		billingStatus: billing?.isBillingNotified,
		reference: transaction.reference ?? "",
		meterNumber: detail.meterNumber,
		accountNumber: detail.accountNumber,
		address: detail.address,
		outstandingBalance: billing?.previousOutstandingBalance ? formatMoney(billing.previousOutstandingBalance) : undefined,
		phoneNumber: transaction.user?.phoneNumber ?? "",
		district: vending?.district ?? billing?.district ?? "N/A",
		attempts: vending ? vending.totalTokenCount ?? 0 : notifyAttempts.length,
		failed: vending ? vending.failedTokenCount ?? 0 : notifyAttempts.filter(a => !a.isSuccess && !!a.errorMessage).length,
		remoteTokenLoadReference: vending?.vendingToken?.vendingRemoteTokenLoadId,
		lastAttemptReference: notifyAttempts[notifyAttempts.length - 1]?.id,
	};
};

const BulkPurchaseDetailsPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { reference } = useParams<RouteParams>();
	const { updateLayout } = useConfigLayout();
	const [searchTerm, setSearchTerm] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedDetail, setSelectedDetail] = useState<CombinedDetail | null>(null);
	const locationState = (location.state as BulkPurchaseLocationState) ?? null;
	const bulkPurchaseSearch = locationState?.bulkPurchaseSearch;

	const handleNavigateBack = useCallback(() => {
		const targetSearch = bulkPurchaseSearch ? `?${bulkPurchaseSearch}` : "";
		const isUtilityAdminRoute = location.pathname.includes(`/${ApplicationRoutes.ADMIN_UTIL_BASE}/`);
		const basePath = isUtilityAdminRoute
			? `/${ApplicationRoutes.ADMIN_UTIL_BASE}/${ApplicationRoutes.BILLS_OPERATIONS}/${ApplicationRoutes.BULK_PURCHASE}`
			: `/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.ADMIN_BILLS}/${ApplicationRoutes.BULK_PURCHASE}`;

		navigate({
			pathname: basePath,
			search: targetSearch,
		});
	}, [navigate, bulkPurchaseSearch, location.pathname]);

	useEffect(() => {
		updateLayout({
			showCountrySelector: false,
			showBreadCrumb: false,
			utility: null,
			showExportButton: false,
			goBackHandler: handleNavigateBack,
		});

		return () => {
			updateLayout({ goBackHandler: null });
		};
	}, [handleNavigateBack, updateLayout]);

	const {
		data,
		isLoading,
		error,
		refetch,
	} = useGetBulkPurchaseByIdQuery(reference ?? "", { skip: !reference });

	const [reVend, { isLoading: isReVending }] = useReVendMutation();
	const [notifyBilling, { isLoading: isNotifyingBilling }] = useNotifyBillingMutation();

	const [downloadInvoice, { data: pdfBlob, isLoading: isDownloadingInvoice }] = useLazyDownloadBulkPurchaseInvoiceQuery();

	const combinedDetails = useMemo(() => {
		if (!data) {
			return [] as CombinedDetail[];
		}

		const rows: CombinedDetail[] = [];

		const billingList = data.bulkPurchaseDetails?.billingDetailsList ?? [];
		billingList.forEach((detail, index) => {
			if (!detail) {
				return;
			}
			rows.push(mapBillingDetailToRow(detail, `billing-${index}`));
		});
		if (data.billingDetails) {
			const existsInList = billingList.some((detail) => detail && detail.id === data.billingDetails?.id);
			if (!existsInList) {
				rows.push(mapBillingDetailToRow(data.billingDetails, "billing-single"));
			}
		}

		const vendingList = data.bulkPurchaseDetails?.vendingDetailsList ?? [];
		vendingList.forEach((detail, index) => {
			if (!detail) {
				return;
			}
			rows.push(mapVendingDetailToRow(detail, `vending-${index}`));
		});
		if (data.vendingDetails) {
			const existsInVendingList = vendingList.some((detail) => detail && detail.id === data.vendingDetails?.id);
			if (!existsInVendingList) {
				rows.push(mapVendingDetailToRow(data.vendingDetails, "vending-single"));
			}
		}

		return rows;
	}, [data]);

	useEffect(() => {
		if (!selectedDetail) {
			return;
		}

		const stillExists = combinedDetails.some((detail) => detail.id === selectedDetail.id);
		if (!stillExists) {
			setSelectedDetail(null);
		}
	}, [combinedDetails, selectedDetail]);

	const normalizedSearchTerm = searchTerm.trim().toLowerCase();

	const filteredDetails = useMemo(() => {
		if (!normalizedSearchTerm) {
			return combinedDetails;
		}
		return combinedDetails.filter((detail) =>
			detail.accountNumber.toLowerCase().includes(normalizedSearchTerm) ||
			detail.meterNumber.toLowerCase().includes(normalizedSearchTerm) ||
			detail.accountName.toLowerCase().includes(normalizedSearchTerm) ||
			detail.address.toLowerCase().includes(normalizedSearchTerm) ||
			detail.typeLabel.toLowerCase().includes(normalizedSearchTerm)
		);
	}, [combinedDetails, normalizedSearchTerm]);

	useEffect(() => {
		setCurrentPage(1);
	}, [normalizedSearchTerm]);

	useEffect(() => {
		const totalPages = Math.max(1, Math.ceil(filteredDetails.length / DETAILS_PAGE_SIZE));
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [filteredDetails.length, currentPage]);

	const paginatedDetails = useMemo(() => {
		const startIndex = (currentPage - 1) * DETAILS_PAGE_SIZE;
		return filteredDetails.slice(startIndex, startIndex + DETAILS_PAGE_SIZE);
	}, [filteredDetails, currentPage]);

	const hasCombinedDetails = combinedDetails.length > 0;
	const hasFilteredDetails = filteredDetails.length > 0;
	const emptyDetailsMessage = hasCombinedDetails
		? "No matching details found for your search."
		: "No details available for this bulk purchase.";

	const renderTableRows = () => {
		if (isLoading) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>Loading bulk purchase details...</td>
				</tr>
			);
		}

		if (error) {
			return (
				<tr className={`${styles.tableMessageRow} ${styles.tableMessageRowError}`}>
					<td colSpan={7}>Unable to load bulk purchase details.</td>
				</tr>
			);
		}

		if (paginatedDetails.length === 0) {
			return (
				<tr className={styles.tableMessageRow}>
					<td colSpan={7}>{emptyDetailsMessage}</td>
				</tr>
			);
		}

		return paginatedDetails.map((detail) => (
			<tr key={detail.id}>
				<td>{detail.accountNumber}</td>
				<td>{detail.meterNumber}</td>
				<td>{detail.accountName}</td>
				<td className={styles.numericCell}>{formatMoney(detail.amount) ?? "-"}</td>
				<td>
					<span className={`${styles.typeBadge} ${detail.typeLabel === "Prepaid" ? styles.typePrepaid : styles.typePostpaid}`}>
						{detail.typeLabel}
					</span>
				</td>
				<td>{detail.address}</td>
				<td className={styles.actionsCell}>
					<button
						type="button"
						className={styles.viewButton}
						onClick={() => handleOpenDetailModal(detail)}
						aria-label={`View details for ${detail.accountName}`}
					>
						<EyeIcon className={styles.viewIcon} />
						View
					</button>
				</td>
			</tr>
		));
	};

	const handleOpenDetailModal = useCallback((detailItem: CombinedDetail) => {
		setSelectedDetail(detailItem);
	}, []);

	const handleCloseDetailModal = useCallback(() => {
		setSelectedDetail(null);
	}, []);

	// Both retries act on the single detail the admin opened, never on the whole bulk.
	const handleRevend = useCallback(() => {
		const vendingDetailsId = selectedDetail?.vendingDetail?.id;

		if (!vendingDetailsId) {
			return;
		}

		reVend({ vendingDetailsId })
			.unwrap()
			.then(() => {
				toast.success("Vending Successful");
				refetch();
			})
			.catch((err) => {
				toast.error(getErrorMessage(err?.data));
			});
	}, [selectedDetail, reVend, refetch]);

	const handleNotifyBilling = useCallback(() => {
		const billingDetailId = selectedDetail?.billingDetail?.id;

		if (!billingDetailId) {
			return;
		}

		notifyBilling({ billingDetailId })
			.unwrap()
			.then(() => {
				toast.success("Billing Notified");
				refetch();
			})
			.catch((err) => {
				toast.error(getErrorMessage(err?.data));
			});
	}, [selectedDetail, notifyBilling, refetch]);

	const handleDownloadInvoice = useCallback(() => {
		if (!reference) {
			return;
		}
		downloadInvoice(reference);
	}, [reference, downloadInvoice]);

	useEffect(() => {
		if (pdfBlob && !isDownloadingInvoice) {
			const url = URL.createObjectURL(pdfBlob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `BulkPurchaseInvoice_${reference}.pdf`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		}
	}, [pdfBlob, isDownloadingInvoice, reference]);

	const statusLabel = data ? getStatusLabel(data.status) : "-";
	const statusVariant = getStatusVariant(data?.status);
	const paymentMethodLabel = data ? (data.type === 2 ? "Cash Payment" : "Payment Hub") : "-";
	const amountLabel = formatMoney(data?.amount) ?? "-";
	const createdAtLabel = formatDateTime(data?.createdAt);
	const totalEntries = combinedDetails.length;

	return (
		<BillPageLayout title="Bulk Purchases">
			<div className={styles.detailsWrapper}>
				{/* <div className={styles.toolbar}>
					<Button
						leftIcon={<BackIcon />}
						variant="outline"
						text="Back to Bulk Purchases"
						onClick={handleNavigateBack}
					/>
				</div> */}

				<div className={styles.headerSection}>
					<div>
						<h2 className={styles.referenceTitle}>Bulk Purchase Details</h2>
						{data?.reference && (
							<p className={styles.referenceSubtitle}>Reference: {data.reference}</p>
						)}
					</div>
					<Button
						leftIcon={<DownloadIcon />}
						variant="primary"
						text={isDownloadingInvoice ? "Downloading..." : "Download Invoice"}
						onClick={handleDownloadInvoice}
						disabled={!data || data.status !== 2 || isDownloadingInvoice}
					/>
				</div>

				<div className={styles.summaryGrid}>
					<div className={styles.summaryItem}>
						<span className={styles.summaryLabel}>Payment Method</span>
						<span className={styles.summaryValue}>{paymentMethodLabel}</span>
					</div>
					<div className={styles.summaryItem}>
						<span className={styles.summaryLabel}>Status</span>
						<span className={`${styles.statusBadge} ${styles[`status${statusVariant.charAt(0).toUpperCase() + statusVariant.slice(1)}`] ?? ""}`}>
							<span className={styles.statusDot} />
							{statusLabel}
						</span>
					</div>
					<div className={styles.summaryItem}>
						<span className={styles.summaryLabel}>Amount</span>
						<span className={styles.summaryValue}>{amountLabel}</span>
					</div>
					<div className={styles.summaryItem}>
						<span className={styles.summaryLabel}>Date &amp; Time</span>
						<span className={styles.summaryValue}>{createdAtLabel}</span>
					</div>
					<div className={styles.summaryItem}>
						<span className={styles.summaryLabel}>Total Entries</span>
						<span className={styles.summaryValue}>{totalEntries}</span>
					</div>
				</div>

				<div className={styles.searchContainer}>
					<div className={styles.searchInput}>
						<input
							type="text"
							placeholder="Search by account, meter or address"
							value={searchTerm}
							onChange={(event) => setSearchTerm(event.target.value.trim())}
						/>
						<span className={styles.searchIcon}>
							<SearchIcon />
						</span>
					</div>
				</div>

				<div className={styles.tableWrapper}>
					<table className={styles.detailsTable}>
						<thead>
							<tr>
								<th>Account Number</th>
								<th>Meter Number</th>
								<th>Account Name</th>
								<th className={styles.numericHeader}>Amount</th>
								<th>Type</th>
								<th>Address</th>
								<th className={styles.actionsHeader}>Action</th>
							</tr>
						</thead>
						<tbody>{renderTableRows()}</tbody>
					</table>
				</div>
				{!isLoading && !error && hasFilteredDetails && (
					<div className={styles.paginationContainer}>
						<TablePagination
							totalItems={filteredDetails.length}
							itemsPerPage={DETAILS_PAGE_SIZE}
							currentPage={currentPage}
							onPageChange={setCurrentPage}
							isLoading={isLoading}
						/>
					</div>
				)}
			</div>
			{selectedDetail && data && (
				<CommonSideDialog onCloseDialog={handleCloseDetailModal}>
					<TransactionDetails
						transactionType={selectedDetail.vendingDetail ? "vending" : "billing"}
						transaction={mapDetailToTransactionData(selectedDetail, data)}
						timeline={
							selectedDetail.vendingDetail
								? mapTokenTimeline(selectedDetail.vendingDetail.tokenTimeline)
								: mapNotifyTimeline(selectedDetail.billingDetail?.notifyTimeline)
						}
						onClose={handleCloseDetailModal}
						onRevend={selectedDetail.vendingDetail?.id ? handleRevend : undefined}
						onNotifyBilling={selectedDetail.billingDetail?.id ? handleNotifyBilling : undefined}
						reVendLoading={isReVending}
						notifyBillingLoading={isNotifyingBilling}
					/>
				</CommonSideDialog>
			)}
		</BillPageLayout>
	);
};

export default BulkPurchaseDetailsPage;





